/* eslint-disable max-classes-per-file */
import { makeAutoObservable, toJS } from 'mobx';
import ISettings, {
  INfeSettings,
  INfceSettings,
  INfseSettings,
  ICteSettings,
  IMdfeSettings,
  ICertificate,
  IEmailSettings,
  IEmailTemplate,
} from '@interfaces/ISettings';
import EAmbiente from '@enums/EAmbiente';

class CertificadoStore implements ICertificate {
  serial: string;

  subject: string;

  expirationDate: Date;

  constructor(data: ICertificate) {
    makeAutoObservable(this);
    if (data) Object.assign(this, data);
  }

  update(data: ICertificate) {
    if (!data) return;

    if (data.serial) this.serial = data.serial;
    if (data.subject) this.subject = data.subject;
    if (data.expirationDate) this.expirationDate = data.expirationDate;
  }
}

class NfeStore implements INfeSettings {
  enabled = false;

  ambiente = EAmbiente.PRODUCAO;

  certificado: CertificadoStore = null;

  // Campos de controle que não vêm do banco
  uploadCertificado: any = null;

  removeCertificado = false;

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<NfeStore>) {
    if (!data) {
      this.enabled = false;
      return;
    }

    if (data.enabled !== undefined) {
      this.enabled = data.enabled;
    } else {
      this.enabled = true;
    }

    if (data.ambiente) this.ambiente = data.ambiente;

    if (data.certificado) {
      // Se já existe uma instância de certificado, atualiza ela, senão cria
      if (this.certificado) {
        this.certificado.update(data.certificado);
      } else {
        this.certificado = new CertificadoStore(data.certificado);
      }
    } else {
      this.certificado = null;
    }
  }

  setAmbiente(ambiente: EAmbiente) {
    this.ambiente = ambiente;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  // Métodos de ação (importante para manter a lógica dentro da classe)
  setUploadCertificado(file: File, password: string) {
    if ((!file || file.size === 0) && !password) {
      this.uploadCertificado = undefined;
    } else {
      this.uploadCertificado = {
        file,
        password,
      };
    }
  }

  setRemoverCertificado(remover: boolean) {
    this.removeCertificado = remover;
    this.uploadCertificado = undefined;
  }
}

class NfceStore implements INfceSettings {
  enabled = false;

  ambiente = EAmbiente.PRODUCAO;

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<NfceStore>) {
    if (!data) {
      this.enabled = false;
      return;
    }

    if (data.enabled !== undefined) {
      this.enabled = data.enabled;
    } else {
      this.enabled = true;
    }

    if (data.ambiente) this.ambiente = data.ambiente;
  }

  setAmbiente(ambiente: EAmbiente) {
    this.ambiente = ambiente;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }
}

class NfseStore implements INfseSettings {
  enabled = false;

  ambiente = EAmbiente.PRODUCAO;

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<NfseStore>) {
    if (!data) {
      this.enabled = false;
      return;
    }

    if (data.enabled !== undefined) {
      this.enabled = data.enabled;
    } else {
      this.enabled = true;
    }

    if (data.ambiente) this.ambiente = data.ambiente;
  }

  setAmbiente(ambiente: EAmbiente) {
    this.ambiente = ambiente;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }
}

class CteStore implements ICteSettings {
  enabled = false;

  ambiente = EAmbiente.PRODUCAO;

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<NfseStore>) {
    if (!data) {
      this.enabled = false;
      return;
    }

    if (data.enabled !== undefined) {
      this.enabled = data.enabled;
    } else {
      this.enabled = true;
    }

    if (data.ambiente) this.ambiente = data.ambiente;
  }

  setAmbiente(ambiente: EAmbiente) {
    this.ambiente = ambiente;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }
}

class MdfeStore implements IMdfeSettings {
  enabled = false;

  ambiente = EAmbiente.PRODUCAO;

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<NfseStore>) {
    if (!data) {
      this.enabled = false;
      return;
    }

    if (data.enabled !== undefined) {
      this.enabled = data.enabled;
    } else {
      this.enabled = true;
    }

    if (data.ambiente) this.ambiente = data.ambiente;
  }

  setAmbiente(ambiente: EAmbiente) {
    this.ambiente = ambiente;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }
}

class EmailTemplateStore implements IEmailTemplate {
  enabled = false;

  subject = '';

  body = '';

  replyTo = '';

  cc = '';

  attachXml = true;

  attachPdf = true;

  autoSend = false;

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<IEmailTemplate>) {
    if (!data) return;
    if (data.enabled !== undefined) this.enabled = data.enabled;
    if (data.subject !== undefined) this.subject = data.subject;
    if (data.body !== undefined) this.body = data.body;
    if (data.replyTo !== undefined) this.replyTo = data.replyTo;
    if (data.cc !== undefined) this.cc = data.cc;
    if (data.attachXml !== undefined) this.attachXml = data.attachXml;
    if (data.attachPdf !== undefined) this.attachPdf = data.attachPdf;
    if (data.autoSend !== undefined) this.autoSend = data.autoSend;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  setSubject(subject: string) {
    this.subject = subject;
  }

  setBody(body: string) {
    this.body = body;
  }

  setReplyTo(replyTo: string) {
    this.replyTo = replyTo;
  }

  setCc(cc: string) {
    this.cc = cc;
  }

  setAttachXml(attachXml: boolean) {
    this.attachXml = attachXml;
  }

  setAttachPdf(attachPdf: boolean) {
    this.attachPdf = attachPdf;
  }

  setAutoSend(autoSend: boolean) {
    this.autoSend = autoSend;
  }
}

class EmailStore implements IEmailSettings {
  smtp = {
    host: '',
    port: undefined as undefined,
    secure: false,
    user: '',
    pass: '',
  };

  limitPerHour: number | undefined = undefined;

  templates = {
    nfe: new EmailTemplateStore(),
    nfce: new EmailTemplateStore(),
    nfse: new EmailTemplateStore(),
    cte: new EmailTemplateStore(),
    mdfe: new EmailTemplateStore(),
  };

  constructor() {
    makeAutoObservable(this);
  }

  update(data?: Partial<IEmailSettings>) {
    if (!data) return;
    if (data.smtp) {
      Object.assign(this.smtp, data.smtp);
    }
    if (data.limitPerHour !== undefined) {
      this.limitPerHour = data.limitPerHour;
    }
    if (data.templates) {
      if (data.templates.nfe) this.templates.nfe.update(data.templates.nfe);
      if (data.templates.nfce) this.templates.nfce.update(data.templates.nfce);
      if (data.templates.nfse) this.templates.nfse.update(data.templates.nfse);
      if (data.templates.cte) this.templates.cte.update(data.templates.cte);
      if (data.templates.mdfe) this.templates.mdfe.update(data.templates.mdfe);
    }
  }

  // setSmtp(field: keyof EmailStore['smtp'], value: any) {
  //   this.smtp[field] = value;
  // }

  setSmtp(field: keyof IEmailSettings['smtp'], value: any) {
    (this.smtp[field] as any) = value;
  }

  setLimitPerHour(value: number | undefined) {
    this.limitPerHour = value;
  }
}

export class ConfigStore implements ISettings {
  loading = false;

  authenticated = false;

  document: string;

  nfe = new NfeStore();

  nfce = new NfceStore();

  nfse = new NfseStore();

  cte = new CteStore();

  mdfe = new MdfeStore();

  email = new EmailStore();

  constructor() {
    makeAutoObservable(this);
  }

  setAuthenticated(authenticated: boolean) {
    this.authenticated = authenticated;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  loadFromBackend(doc: any) {
    if (!doc) return;

    // Passamos o objeto específico para cada classe
    // Se doc.nfe for undefined, o constructor da classe trata
    this.nfe.update(doc.nfe);
    this.nfce.update(doc.nfce);
    this.nfse.update(doc.nfse);
    this.cte.update(doc.cte);
    this.mdfe.update(doc.mdfe);
    this.email.update(doc.email);
  }

  // O método que limpa o objeto para salvar no Mongo
  toJSON() {
    const out = toJS(this);

    delete out.loading;
    delete out.authenticated;
    if (!out?.nfe?.uploadCertificado?.file || out.nfe.removeCertificado)
      delete out.nfe.certificado;

    return out;
  }

  reset() {
    this.nfe.update();
    this.nfce.update();
    this.nfse.update();
    this.cte.update();
    this.mdfe.update();
    this.authenticated = false;
  }

  async load(api: any) {
    try {
      this.setLoading(true);
      const response = await api.get('/settings');
      this.loadFromBackend(response.data);
      this.setAuthenticated(true);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        this.setAuthenticated(false);
      }
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  get models(): string[] {
    const models = [];
    if (this.nfe.enabled) models.push('55');
    if (this.nfce.enabled) models.push('65');
    if (this.nfse.enabled) models.push('NS');
    if (this.cte.enabled) {
      models.push('57');
      models.push('67');
    }
    if (this.mdfe.enabled) models.push('58');

    return models;
  }
}

// Exporta uma única instância para o projeto (Singleton)
const store = new ConfigStore();
export default store;
